import React, { FC, useEffect, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { ResourceMapping } from '@ariksa/inventory-core';
import { Box, Divider, Stack, HStack } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import { INIT_PAGE_INFO } from 'services/utils/constants';

import { selectCompliance } from 'app/containers/Compliance/selectors';
import { actions } from 'app/containers/Compliance/slice';
import {
  DetailsIdentifier,
  ChakraMarkdown,
  ExpandableText,
  renderRiskContext,
  renderSeverityBar,
  renderTableHeaderWithLoader,
  StackedCell,
  StackedHeader,
  WithResourceIcon,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { Sorted } from 'components/DataDisplay/NewTable/types';
import { Select } from 'components/DataEntry';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { Drawer } from 'components/Overlay';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import {
  ResourceTypeIconTooltip,
  useResourceType,
} from 'containers/App/hooks/useResourceType';
import { EnvironmentName } from 'containers/App/utils';
import { ComplianceAddException } from 'containers/Compliance/Components/AddException/ComplianceAddException';
import {
  complianceStatusOptions,
  renderComplianceStatus,
} from 'containers/Compliance/Components/ComplianceTables/utils';
import { History } from 'containers/Compliance/Components/History';
import { useCompliance } from 'containers/Compliance/Components/hooks/context';
import { SeverityComponent } from 'containers/Compliance/Components/SeverityComponent';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

interface Props {
  row: Record<string, any>;
  subControl?: Record<string, any>;
  isOpen: boolean;
  onClose: () => void;
}

export const ResourcesUnderPolicyDrawer: FC<Props> = props => {
  const { row, isOpen, onClose, subControl } = props;
  const dispatch = useDispatch();
  const { rulesResources, selectedBlueprint, environment } = useSelector(
    selectCompliance,
  );
  const { riskContext } = useSelector(selectSharedState);

  const {
    renderRulesActions,
    isOpenSeverityModal,
    isOpenSuppress,
    onCloseSuppress,
    selectedResource,
    isOpenHistory,
  } = useCompliance();
  const { renderStackedCloudAccountIdWithIcon } = useCloudAccountId();
  const [summary, setSummary] = useState<{
    data: Record<string, any>[];
    total: number;
  }>({ data: [], total: 0 });
  const [pageInfo, setPageInfo] = useState(INIT_PAGE_INFO);
  const [status, setStatus] = useState<Record<string, any>>({});
  const [sortByField, setSortByField] = useState<Sorted>({
    sortField: '',
    sortOrder: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [resourceType, setResourceType] = useState<ResourceMapping>(
    {} as ResourceMapping,
  );

  const { getResourceType } = useResourceType();
  const { onOpenMetadata } = useActiveResourceContext();
  const { updateActiveResource } = useActiveResourceActions();
  const { getTabIndex } = useMetadataDrawerTabs();
  const [alertTabIndex, setAlertTabIndex] = useState(0);

  //get rules
  useDeepCompareEffect(() => {
    !!selectedBlueprint?.id &&
      dispatch(
        actions.getComplianceSummaryByRule({
          q: {
            blueprintId: selectedBlueprint?.id,
            ruleId: row?.rule_id,
            standardId: row?.id,
            page: pageInfo.page_number,
            size: pageInfo.page_size,
            show: status?.value,
            sortBy: sortByField.sortField || undefined,
            sortOrder: sortByField.sortOrder || undefined,
            searchTerm: searchTerm || undefined,
            environmentId: environment?.id,
          },
          onSuccess: res => {
            setSummary({ data: res?.items || [], total: res?.total || 0 });
            dispatch(
              sharedStateActions.getRiskContext({
                q: {
                  riskContextRequest: {
                    uuids: map(res?.items, o => o.resource_id),
                  },
                },
              }),
            );
          },
        }),
      );
  }, [
    dispatch,
    row,
    selectedBlueprint,
    environment,
    pageInfo,
    status,
    searchTerm,
  ]);

  //set resource type
  useEffect(() => {
    setResourceType(getResourceType(row?.native_resource)!);
  }, [getResourceType, row]);

  //set metadata drawer alerts tab index
  useEffect(() => {
    setAlertTabIndex(
      getTabIndex(resourceType.cloud_native_class!, MetadataTabs.Alerts),
    );
  }, [getTabIndex, resourceType]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Entity</Box>,
      accessor: 'name',
      align: 'left',
      sortKey: 'name',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={row?.type}
          iconTooltip={<ResourceTypeIconTooltip resourceType={row?.type} />}
          iconSize="9"
        >
          <StackedCell upper={row?.name} lower={row?.cloud_resource_id} />
        </WithResourceIcon>
      ),
    },
    {
      header: 'Entity Type',
      accessor: 'cloud_resource_id',
      align: 'left',
      render: ({ row }) => {
        const type = getResourceType(row?.type);
        return (
          <StackedCell
            upper={type?.cloud_native_name}
            lower={type?.cloud_agnostic_name}
          />
        );
      },
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
    {
      header: 'Account',
      accessor: 'account_id',
      align: 'left',
      render: ({ row }) => renderStackedCloudAccountIdWithIcon(row?.account_id),
      styles: { width: '220px', cell: { maxWidth: '220px' } },
    },

    {
      header: 'Exception',
      accessor: 'check_stats.resource_exceptions',
      align: 'center',
      render: ({ value }) => (!!value ? 'Yes' : 'No'),
      styles: { width: '125px', cell: { maxWidth: '125px' } },
    },
    {
      header: renderTableHeaderWithLoader(
        'Risk context',
        riskContext.isLoading,
      ),
      align: 'left',
      render: ({ row }) =>
        renderRiskContext(riskContext.data?.[row?.resource_id]),
      styles: { width: '135px', cell: { maxWidth: '135px' } },
    },
    {
      header: 'Status',
      accessor: 'check_stats.failed',
      render: renderComplianceStatus,
      styles: { width: '80px', cell: { maxWidth: '80px' } },
      align: 'left',
    },
    {
      header: 'Actions',
      render: renderRulesActions,
      styles: { width: '120px', cell: { maxWidth: '120px' } },
    },
  ];

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        header={
          <HStack justify="space-between">
            <StackedHeader
              upper={row?.description}
              lower={row?.name}
              type={row.native_resource}
            />
          </HStack>
        }
        body={
          <Stack w="full" h="full" spacing={5}>
            <HStack spacing={8} align="start">
              <Box w="67%">
                <DetailsIdentifier
                  label="Description"
                  value={
                    <ExpandableText noOfLines={3}>
                      <ChakraMarkdown
                        content={
                          subControl?.sub_control_description ??
                          row?.description ??
                          '-'
                        }
                      />
                    </ExpandableText>
                  }
                  isInline={false}
                  spacing={2}
                />
              </Box>
              <Divider
                orientation="vertical"
                alignSelf="stretch"
                borderColor="componentBorder"
              />
              <Stack>
                <DetailsIdentifier label="Cloud" />
                <DetailsIdentifier
                  label="Resource Type"
                  value={
                    <WithResourceIcon
                      resourceType={row?.native_resource}
                      iconSize="xs"
                    >
                      {resourceType.cloud_native_name}
                    </WithResourceIcon>
                  }
                  align="center"
                />
                <DetailsIdentifier
                  label="Severity"
                  value={renderSeverityBar({ value: row?.severity })}
                />
                <DetailsIdentifier
                  label="Environment"
                  value={<EnvironmentName environmentId={environment?.id} />}
                  align="center"
                />
              </Stack>
            </HStack>

            <BorderedBox flex={1} h="full" p={4}>
              <Stack spacing={5} h="full">
                <HStack>
                  <Box w={96} pr={2}>
                    <SearchBox
                      onChange={value => setSearchTerm(value)}
                      value={searchTerm}
                      placeholder="Search"
                    />
                  </Box>
                  <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
                  <HStack w="full">
                    <Box>
                      <Select
                        options={complianceStatusOptions}
                        value={status}
                        onChange={setStatus}
                        showIconInValueContainer
                        menuPortalTarget={document.body}
                        isAFilter
                        leftMessage="Status"
                        showUndefinedValueLabel={false}
                      />
                    </Box>
                  </HStack>
                </HStack>
                <Box flex={1} w="full" h="full">
                  <Table
                    data={summary.data}
                    columns={columns}
                    cursor="pointer"
                    isLoading={rulesResources.isLoading}
                    pagination={{
                      totalCount: summary.total,
                      pageInfo,
                      onChange: setPageInfo,
                    }}
                    onRowClick={row => {
                      updateActiveResource({
                        resourceType: row?.type,
                        uuid: row.resource_id,
                        accountId: row.account_id,
                        resourceId: row.cloud_resource_id,
                        tabIndex: alertTabIndex,
                        cloudProvider: row?.cloud_provider?.toLowerCase() as CloudProviders,
                      });
                      onOpenMetadata();
                    }}
                    onSort={sortInfo => {
                      if (!isEqual(sortByField, sortInfo)) {
                        setSortByField(sortInfo);
                      }
                    }}
                    sortBy={sortByField}
                  />
                </Box>
              </Stack>
            </BorderedBox>
            {isOpenHistory && (
              <History
                ruleId={row?.rule_id}
                control={subControl?.control}
                subControl={subControl?.sub_control}
                policyDescription={
                  subControl?.sub_control_description ?? row?.description
                }
              />
            )}
            {isOpenSeverityModal && <SeverityComponent />}
            {isOpenSuppress && (
              <ComplianceAddException
                resource={selectedResource}
                policy={subControl ?? row}
                isOpen={isOpenSuppress}
                onClose={onCloseSuppress}
                exceptionFor="PolicyResource"
              />
            )}
          </Stack>
        }
        closeButton
        styles={{
          content: { maxW: '1400px' },
          drawer: {
            blockScrollOnMount: false,
            // The resource metadata drawer can open on top of this drawer from a
            // portal outside it; keep this drawer from locking scroll or trapping
            // focus so the metadata drawer's tables scroll and its selects open.
            trapFocus: false,
          },
        }}
      />
    </>
  );
};
